/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * A 56 px status tile for modals and panels, in place of Coriolis' large status illustrations:
 * loading = LoaderCircle spinning (info), COMPLETED = CircleCheck (green), ERROR = CircleX (red),
 * CONFIRMATION = CircleHelp (amber), anything else = Clock (neutral).
 */

import * as React from "react";
import styled from "styled-components";
import { CircleCheck, CircleHelp, CircleX, Clock, LoaderCircle } from "lucide-react";

import IconTile from "@src/components/ui/IconTile";

const Spin = styled.div`
  svg {
    animation: status-tile-spin 1s linear infinite;
  }
  @keyframes status-tile-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

type Props = { loading?: boolean; status?: string; size?: number; style?: React.CSSProperties };
const StatusTile = ({ loading, status, size = 56, style }: Props) => {
  if (loading) {
    return (
      <Spin style={style} data-test-id="statusTile-loading">
        <IconTile icon={LoaderCircle} size={size} tone="info" />
      </Spin>
    );
  }
  const map: { [k: string]: { icon: typeof Clock; tone: "success" | "danger" | "warning" | "neutral" } } = {
    COMPLETED: { icon: CircleCheck, tone: "success" },
    ERROR: { icon: CircleX, tone: "danger" },
    CONFIRMATION: { icon: CircleHelp, tone: "warning" },
  };
  const info = map[status || ""] || { icon: Clock, tone: "neutral" as const };
  return (
    <div style={style} data-test-id={`statusTile-${status || "neutral"}`}>
      <IconTile icon={info.icon} size={size} tone={info.tone} />
    </div>
  );
};

export default StatusTile;
